import { sentryUtils, trackUserAction, trackAPICall, measurePerformance } from '@/utils/sentry';
import type { Survey, SurveyParticipant } from '@/types/survey';
import type { CreateSurveyRequest, UpdateSurveyRequest } from '@/types/mutation-params';
import type { SeverityLevel } from '@sentry/react';

// Survey-specific Sentry integration
export const surveySentryUtils = {
  // Track survey creation
  trackSurveyCreation: (surveyData: CreateSurveyRequest) => {
    sentryUtils.addBreadcrumb('Survey creation started', 'survey-action', 'info', {
      surveyName: surveyData.surveyName,
      period: surveyData.survey.quarter,
      year: surveyData.survey.year,
      indicatorCount: surveyData.survey.indicators?.length || 0,
      consentType: surveyData.consentType,
    });
  },

  // Add breadcrumb method for direct access
  addBreadcrumb: (message: string, category?: string, level?: SeverityLevel, data?: Record<string, unknown>) => {
    sentryUtils.addBreadcrumb(message, category, level, data);
  },

  // Track survey update
  trackSurveyUpdate: (surveyData: UpdateSurveyRequest) => {
    sentryUtils.addBreadcrumb('Survey update started', 'survey-action', 'info', {
      surveyId: surveyData.survey.surveyId,
      surveyName: surveyData.surveyName,
      changes: Object.keys(surveyData.survey),
    });
  },

  // Track survey deletion
  trackSurveyDeletion: (surveyId: string, surveyName?: string) => {
    sentryUtils.addBreadcrumb('Survey deletion requested', 'survey-action', 'warning', {
      surveyId,
      surveyName,
    });
  },

  // Track survey participant actions
  trackParticipantAction: (action: string, surveyId: string, participantId?: string, data?: Record<string, unknown>) => {
    sentryUtils.addBreadcrumb(`Participant ${action}`, 'participant-action', 'info', {
      surveyId,
      participantId,
      ...data,
    });
  },

  // Track survey responses
  trackSurveyResponse: (participantId: string, surveyId: string, responseCount: number) => {
    sentryUtils.addBreadcrumb('Survey response submitted', 'survey-response', 'info', {
      participantId,
      surveyId,
      responseCount,
    });
  },

  // Track indicator selection
  trackIndicatorSelection: (indicatorId: string, indicatorName: string, action: 'added' | 'removed') => {
    sentryUtils.addBreadcrumb(`Indicator ${action}`, 'indicator-action', 'info', {
      indicatorId,
      indicatorName,
      action,
    });
  },

  // Track consent template changes
  trackConsentTemplateChange: (surveyId: string, consentType: string, hasCustomTemplate: boolean) => {
    sentryUtils.addBreadcrumb('Consent template updated', 'consent-action', 'info', {
      surveyId,
      consentType,
      hasCustomTemplate,
    });
  },

  // Track survey status changes
  trackSurveyStatusChange: (surveyId: string, oldStatus: string, newStatus: string) => {
    sentryUtils.addBreadcrumb('Survey status changed', 'survey-action', 'info', {
      surveyId,
      oldStatus,
      newStatus,
    });
  },

  // Track notification actions
  trackNotificationAction: (action: string, surveyId: string, participantCount?: number) => {
    sentryUtils.addBreadcrumb(`Notification ${action}`, 'notification-action', 'info', {
      surveyId,
      participantCount,
    });
  },

  // Track bulk operations
  trackBulkOperation: (operation: string, surveyId: string, itemCount: number, successCount?: number) => {
    sentryUtils.addBreadcrumb(`Bulk ${operation}`, 'bulk-action', 'info', {
      surveyId,
      itemCount,
      successCount,
      failureCount: successCount !== undefined ? itemCount - successCount : undefined,
    });
  },

  // Track API errors with survey context
  trackSurveyAPIError: (error: Error, endpoint: string, surveyId?: string, context?: Record<string, unknown>) => {
    sentryUtils.captureException(error, {
      tags: {
        feature: 'survey',
        endpoint,
        ...(surveyId && { surveyId }),
      },
      extra: {
        endpoint,
        surveyId,
        ...context,
      },
    });
  },

  // Track performance for survey operations
  measureSurveyOperation: async <T>(
    operationName: string,
    operation: () => Promise<T>,
    context?: Record<string, unknown>
  ): Promise<T> => {
    return measurePerformance(`survey-${operationName}`, async () => {
      if (context) {
        sentryUtils.setContext('survey-operation', context);
      }
      return operation();
    });
  },

  // Set survey context for error tracking
  setSurveyContext: (survey: Survey) => {
    sentryUtils.setContext('survey', {
      surveyId: survey.surveyId,
      surveyName: survey.surveyName,
      status: survey.surveyStatus,
      period: survey.semester,
      year: survey.year,
      indicatorCount: survey.indicators?.length || 0,
      participantCount: survey.participants?.length || 0,
    });
  },

  // Set participant context for error tracking
  setParticipantContext: (participant: SurveyParticipant) => {
    sentryUtils.setContext('participant', {
      participantId: participant.surveyParticipantId,
      firstName: participant.firstName,
      lastName: participant.lastName,
      grade: participant.grade,
      status: participant.participantStatus,
    });
  },

  // Track form validation errors
  trackFormValidationError: (formName: string, fieldName: string, errorMessage: string) => {
    sentryUtils.addBreadcrumb('Form validation error', 'form-error', 'warning', {
      formName,
      fieldName,
      errorMessage,
    });
  },

  // Track user navigation within survey features
  trackSurveyNavigation: (from: string, to: string, surveyId?: string) => {
    sentryUtils.addBreadcrumb(`Survey navigation: ${from} → ${to}`, 'navigation', 'info', {
      from,
      to,
      surveyId,
      feature: 'survey',
    });
  },
};

// Enhanced error tracking for survey API calls
export const trackSurveyAPICall = (
  endpoint: string,
  method: string,
  status?: number,
  duration?: number,
  surveyId?: string
) => {
  trackAPICall(endpoint, method, status, duration);
  
  // Add survey-specific context
  if (surveyId) {
    sentryUtils.setTag('surveyId', surveyId);
  }
};

// Survey-specific user action tracking
export const trackSurveyUserAction = (action: string, data?: Record<string, unknown>) => {
  trackUserAction(action, {
    feature: 'survey',
    ...data,
  });
}; 